export const APP_CONFIG = Object.freeze({
  title: 'Iglesia de Nuestra Señora del Rivero',
  location: 'San Esteban de Gormaz',
  experienceLabel: 'Experiencia inmersiva',
  modelErrorMessage: 'No se ha podido cargar el modelo de la iglesia.',
});

export const START_POSITION = Object.freeze([0, 0, 24]);
export const START_ROTATION = 0;
export const DESKTOP_EYE_HEIGHT = 1.68;
export const SNAP_TURN_ANGLE = 30;

export const XR_CONFIG = Object.freeze({
  referenceSpaceType: 'local-floor',
  requiredFeatures: ['local-floor'],
  optionalFeatures: ['bounded-floor', 'hand-tracking', 'layers'],
  rayLength: 12,
});

export const LOCOMOTION_CONFIG = Object.freeze({
  snapTurnAngle: SNAP_TURN_ANGLE,
  snapDeadzone: 0.72,
  snapReleaseThreshold: 0.35,
  snapCooldown: 0.32,
  smoothEnabled: true,
  smoothSpeed: 1.5,
  moveDeadzone: 0.18,
});

export const TELEPORT_CONFIG = Object.freeze({
  maxDistance: 22,
  visitRadius: 41,
  maxSlopeDegrees: 35,
  markerRadius: 0.34,
  markerColorValid: 0xd8a95b,
  markerColorInvalid: 0xcc554b,
  blockedZones: [
    { minX: -7.3, maxX: 7.3, minZ: -15.7, maxZ: 13.5 },
  ],
});

export const CHURCH_CONFIG = Object.freeze({
  modelUrl: '/models/iglesia.glb',
  scale: [1, 1, 1],
  position: [0, 0, -2],
  rotation: [0, 0, 0],
  castShadow: true,
  receiveShadow: true,
  placeholder: {
    width: 10.5,
    length: 24,
    wallHeight: 7.5,
    naveHeight: 11,
    towerSize: 5.2,
    towerHeight: 17,
    apseRadius: 5.25,
    stoneColor: 0xbca98a,
    stoneAccentColor: 0x8c7659,
    roofColor: 0x6d4433,
    openingColor: 0x211d19,
  },
});

export const ENVIRONMENT_CONFIG = Object.freeze({
  groundSize: 110,
  visitRadius: TELEPORT_CONFIG.visitRadius,
  walkwayWidth: 4.2,
  colors: {
    ground: 0x77835b,
    walkway: 0xb5a17e,
    trunk: 0x5d4934,
    foliage: 0x536b3f,
  },
  vegetation: {
    count: 52,
    seed: 7319,
    innerRadius: 27,
    outerRadius: 48,
    minHeight: 2.8,
    maxHeight: 5.8,
    minScale: 0.72,
    maxScale: 1.35,
  },
});

export const LIGHTING_CONFIG = Object.freeze({
  background: 0x9fc2d4,
  exposure: 1.05,
  fog: { color: 0xb9c9c1, near: 48, far: 105 },
  hemisphere: {
    skyColor: 0xc9e3f0,
    groundColor: 0x5f563f,
    intensity: 1.45,
  },
  sun: {
    color: 0xffe0aa,
    intensity: 3.1,
    position: [-28, 36, 18],
    shadow: {
      mapSize: 1024,
      bias: -0.0003,
      normalBias: 0.025,
      cameraSize: 34,
      near: 1,
      far: 92,
    },
  },
});

export const QUALITY_PRESETS = Object.freeze({
  LOW: {
    maxPixelRatio: 1,
    antialias: false,
    shadows: false,
    shadowMapSize: 512,
    shadowDistance: 28,
    vegetationMultiplier: 0.45,
  },
  MEDIUM: {
    maxPixelRatio: 1.35,
    antialias: true,
    shadows: true,
    shadowMapSize: 1024,
    shadowDistance: 42,
    vegetationMultiplier: 0.72,
  },
  HIGH: {
    maxPixelRatio: 1.75,
    antialias: true,
    shadows: true,
    shadowMapSize: 2048,
    shadowDistance: 58,
    vegetationMultiplier: 1,
  },
});

const provisional = (text) => `Contenido provisional de ejemplo. ${text}`;

export const HOTSPOTS = Object.freeze([
  {
    id: 'portico',
    title: 'Galería porticada',
    description: provisional('Este panel señalará la lectura arquitectónica y el recorrido exterior de la galería cuando se incorpore el contenido historiográfico definitivo.'),
    position: [-7.4, 2.1, -1.5],
    audio: null,
    narration: 'Audio provisional de ejemplo. Este punto está preparado para incorporar la narración histórica definitiva de la galería porticada.',
  },
  {
    id: 'portada',
    title: 'Portada románica',
    description: provisional('Aquí se describirán sus elementos visibles y su relación con la fachada, sin adelantar datos históricos aún no validados.'),
    position: [0, 2.25, 10.35],
    audio: null,
  },
  {
    id: 'abside',
    title: 'Ábside',
    description: provisional('Punto preparado para explicar el volumen del ábside y sus detalles exteriores con fuentes documentales verificadas.'),
    position: [0, 2.2, -15.2],
    audio: null,
  },
  {
    id: 'torre',
    title: 'Torre / campanario',
    description: provisional('Este contenido se sustituirá por una explicación validada del campanario, su construcción y sus transformaciones.'),
    position: [4.1, 9.6, 7.4],
    audio: null,
  },
  {
    id: 'entorno',
    title: 'Entorno de la iglesia',
    description: provisional('Punto reservado para interpretar la implantación del edificio en su paisaje inmediato y el recorrido de visita.'),
    position: [12.5, 1.8, 7.5],
    audio: null,
  },
  {
    id: 'panoramica',
    title: 'Vista panorámica de San Esteban de Gormaz',
    description: provisional('Aquí podrá incorporarse una lectura panorámica documentada y, en el futuro, una fotografía o vídeo de 360 grados.'),
    position: [-15.5, 1.9, 13.5],
    audio: null,
  },
]);

export const UI_CONFIG = Object.freeze({
  panelDistance: 1.75,
  panelWidth: 1.35,
  panelHeight: 0.88,
  panelCanvasWidth: 1280,
  panelCanvasHeight: 832,
  hotspotBaseScale: 1,
  hotspotHoverScale: 1.16,
  hotspotColor: 0xe2b766,
  hotspotHoverColor: 0xffe0a1,
  panelBackground: '#171a19',
  panelForeground: '#f4efe6',
  panelAccent: '#d8a95b',
});
